import { type ConsentChoice, onOpenCookieSettings, saveConsent } from '@nt/consent'
import { useEffect, useId, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { useIsModalOpen } from '@/hooks/useModalOpen'

import { useConsentState } from '../use-consent-state'

import { ConsentPurposeRows } from './consent-purpose-rows'

type View = 'summary' | 'customize'

/**
 * The cookie banner (@nt/consent). A bottom panel, not a modal: the page stays
 * usable and nothing moves under it. Shown to a visitor in the EEA, the UK or
 * Switzerland (the Worker's `nt_geo` cookie) who has not chosen yet, and
 * reopened anywhere by a "Cookie settings" link (openCookieSettings). Accept
 * and Reject carry the same weight on purpose. It sits above everything, so
 * while a modal is open (useIsModalOpen) it steps aside rather than cover the
 * modal's controls, and comes back, still unanswered, when the modal closes.
 */
export function CookieConsentBanner() {
  const consent = useConsentState()
  const [open, setOpen] = useState(consent.showBanner)
  const [view, setView] = useState<View>('summary')
  const [draft, setDraft] = useState<ConsentChoice>(consent.effective)
  const panelRef = useRef<HTMLElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const focusOnOpenRef = useRef(false)
  const id = useId()
  const isModalOpen = useIsModalOpen()

  // The latest effective choice, read inside the window listener below.
  const effectiveRef = useRef(consent.effective)
  effectiveRef.current = consent.effective

  useEffect(
    () =>
      onOpenCookieSettings(() => {
        returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        focusOnOpenRef.current = true
        setDraft(effectiveRef.current)
        setView('customize')
        setOpen(true)
      }, window),
    [],
  )

  // Opened on request (not on first load): take keyboard focus into the panel.
  useEffect(() => {
    if (!open || !focusOnOpenRef.current) return
    focusOnOpenRef.current = false
    panelRef.current?.focus()
  }, [open])

  if (!open || isModalOpen) return null

  const choose = (choice: ConsentChoice) => {
    saveConsent(choice, window)
    setOpen(false)
    returnFocusRef.current?.focus()
    returnFocusRef.current = null
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[2147483001] flex justify-center p-3 sm:p-6">
      <section
        ref={panelRef}
        tabIndex={-1}
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-body`}
        className="pointer-events-auto max-h-dvh w-full max-w-xl overflow-y-auto rounded-card bg-card p-5 shadow-panel outline-none sm:p-6"
      >
        <h2 id={`${id}-title`} className="text-base font-semibold text-foreground">
          Cookies
        </h2>
        <p id={`${id}-body`} className="mt-1 text-sm text-muted-foreground">
          We use cookies to see how people use our site and to measure our ads. You choose which ones we may use. Change
          your choice any time in Settings › Cookies or under Cookie settings.
        </p>

        {view === 'customize' ? (
          <div className="mt-3 divide-y divide-border border-y border-border">
            <ConsentPurposeRows
              id={id}
              draft={draft}
              onChange={(next) => setDraft((previous) => ({ ...previous, ...next }))}
            />
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {view === 'customize' ? (
            <>
              <Button onClick={() => choose(draft)}>Save choices</Button>
              <Button variant="outline" onClick={() => choose({ analytics: true, advertising: true })}>
                Accept all
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => choose({ analytics: true, advertising: true })}>Accept all</Button>
              <Button onClick={() => choose({ analytics: false, advertising: false })}>Reject all</Button>
              <Button variant="ghost" onClick={() => setView('customize')}>
                Customize
              </Button>
            </>
          )}
        </div>
      </section>
    </div>
  )
}
